import { WireAdapter } from "lwc";

import {
  I18n,
  TranslateOptions,
  Locales,
  registerLocaleChangeHandler,
  configure,
} from "@devforce/trailhead-client-locale";

configure((i18n) => {
  // Use "en" labels as a fallback
  i18n.fallbacks = true;
});

export type LabelsAdapterConfig = {
  locales: Locales;
  options?: Record<string, TranslateOptions>;
};

export type LabelsAdapterValue = Record<string, string>;

const translators: WeakMap<Locales, I18n> = new WeakMap();

export class LabelsAdapter implements WireAdapter {
  i18n: I18n = undefined!;

  private config: LabelsAdapterConfig | null = null;

  private get options() {
    if (!this.config) return {};
    return this.config.options || {};
  }

  private get labels() {
    if (!this.config) return {};
    let keys = flattenKeys(this.config.locales.en!);
    return {
      ...keys.reduce(
        (labels, key) =>
          Object.assign(labels, {
            [key]: this.i18n.t("default", key, this.options[key] || {}),
          }),
        {}
      ),
    };
  }

  private removeLocaleChangeHandler: () => void = () => {};

  private setValue: (value: LabelsAdapterValue) => void;

  constructor(setValue: (value: LabelsAdapterValue) => void) {
    this.setValue = setValue;
  }

  connect() {
    this.setValue(this.labels);
    this.removeLocaleChangeHandler = registerLocaleChangeHandler(() => {
      this.setValue(this.labels);
    });
  }

  disconnect() {
    this.removeLocaleChangeHandler();
  }

  update(config: LabelsAdapterConfig) {
    if (!this.config || config.locales !== this.config.locales) {
      this.i18n =
        translators.get(config.locales) ||
        translators.set(config.locales, new I18n()).get(config.locales)!;
      this.i18n.addScope({ default: config.locales });
    }
    this.config = { ...config };
    this.setValue(this.labels);
  }
}

export function flattenKeys(
  object: Record<string, unknown>,
  keyPath: string = ""
): string[] {
  return Object.keys(object).flatMap((key) => {
    if (typeof object[key] === "object") {
      return flattenKeys(
        object[key] as Record<string, unknown>,
        `${keyPath}${key}.`
      );
    }
    return `${keyPath}${key}`;
  });
}
